/**
 * Anonymize an IBAN by masking all but the last 4 digits.
 *
 * @param iban - IBAN to anonymize (with or without spaces)
 * @returns Anonymized IBAN in format "**** **** **** XXXX"
 *
 * @example
 * anonymizeIban("FR1420041010050500013M02606") // "**** **** **** 2606"
 * anonymizeIban("FR14 2004 1010 0505 0001 3M02 606") // "**** **** **** 2606"
 * anonymizeIban("") // "****"
 * anonymizeIban("ABC") // "*ABC"
 */
export function anonymizeIban(iban: string): string {
    const normalizedIban = iban.replace(/\s/g, "")

    if (normalizedIban.length <= 4) {
        return normalizedIban.padStart(4, "*")
    }

    const lastFourDigits = normalizedIban.slice(-4)

    return `**** **** **** ${lastFourDigits}`
}

/**
 * Compare two IBANs, ignoring whitespace.
 *
 * Note: the comparison is case sensitive, so an IBAN written in lowercase does
 * not match its uppercase counterpart. Use `prepareIban` upstream if you need
 * case to be normalized too.
 *
 * @param iban - IBAN to compare (with or without spaces)
 * @param otherIban - IBAN to compare against (with or without spaces)
 * @returns `true` when both IBANs are the same
 *
 * @example
 * isSameIban("FR14 2004 1010", "FR1420041010") // true
 * isSameIban("fr1420041010", "FR1420041010") // false
 */
export function isSameIban(iban: string, otherIban: string): boolean {
    return iban.replace(/\s/g, "") === otherIban.replace(/\s/g, "")
}
